/**
 * WelcomeHub — post-login product hub.
 *
 * Shown only to users who have:
 *   1. Successfully signed in via Cognito
 *   2. Acknowledged all 5 legal documents (Terms, Privacy, Risk, FX Risk, NDPR)
 *
 * The hub presents the four primary trading products as large cards and
 * three operational tools as smaller tiles. Selecting a card routes the user
 * into the Terminal component with that product pre-selected (via the
 * initialView prop). All products route through the React SPA.
 */
import { useAuth } from '../auth/AuthContext';
import type { RailView } from './LeftRail';

interface Props {
  onChooseProduct: (initialView: RailView) => void;
}

interface LargeCard {
  key: RailView | 'fx-external';
  title: string;
  subtitle: string;
  description: string;
  icon: string;
  accent: string;
}

interface SmallTile {
  key: RailView;
  title: string;
  icon: string;
}

const LARGE_CARDS: LargeCard[] = [
  {
    key: 'Dashboard',
    title: 'Dashboard',
    subtitle: 'Workspace hub',
    description: 'Launch any workspace in the trader. Everything is reachable from here.',
    icon: '⌂',
    accent: '#00d4ff', // cyan
  },
  {
    key: 'Trade',
    title: 'Equity Trader',
    subtitle: 'NGX listed equities',
    description: 'Full-depth Level II order book, multi-broker SOR, and pre-trade compliance.',
    icon: '◆',
    accent: '#27c281', // green
  },
  {
    key: 'fx-external',
    title: 'FX Trader',
    subtitle: 'Synthetic NGN pairs',
    description: 'USD/NGN at 22-pip headline spreads plus Tier-A African crosses. Private beta.',
    icon: '◉',
    accent: '#ff7a3d', // orange
  },
  {
    key: 'Bond',
    title: 'Bond Trader',
    subtitle: 'FGN, Sukuk, T-Bills',
    description: 'Government and corporate fixed income across the full Nigerian curve.',
    icon: '▤',
    accent: '#5fa0ff', // blue
  },
  {
    key: 'ETF',
    title: 'ETF Trader',
    subtitle: 'NGX exchange-traded funds',
    description: '11 NGX-listed ETFs across broad market, sector, commodity, and Sharia themes.',
    icon: '▦',
    accent: '#c47fff', // purple
  },
];

const SMALL_TILES: SmallTile[] = [
  { key: 'Tools', title: 'Tools', icon: '⚙' },
  { key: 'Brokers', title: 'Brokers', icon: '⇋' },
  { key: 'Funding', title: 'Funding', icon: '₦' },
];

function greetingForHour(date: Date): string {
  const h = date.getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

function firstNameFrom(username?: string): string {
  if (!username) return '';
  // Cognito sub looks like a UUID-ish string. If the username is not an email
  // or looks like an opaque ID, fall back to empty so we just say "Welcome back".
  if (username.includes('@')) {
    const local = username.split('@')[0] ?? '';
    // Try to extract meaningful name from email - use full part before @ or first part before dot
    const parts = local.split('.');
    if (parts.length > 1) {
      // If there are multiple parts (like tunmishe.oke), try to get the longest/most meaningful
      return parts.find(p => p.length > 3) ?? parts[0] ?? '';
    }
    return local;
  }
  if (/^[a-z0-9-]{20,}$/i.test(username)) return '';
  return username.split(' ')[0] ?? '';
}

export function WelcomeHub({ onChooseProduct }: Props) {
  const auth = useAuth();
  const greeting = greetingForHour(new Date());
  // Use displayName from Cognito if available, otherwise extract from username
  const name = auth.displayName || firstNameFrom(auth.username);

  const handleLargeCard = (card: LargeCard) => {
    const routeMap: { [key: string]: RailView } = {
      'Dashboard': 'Dashboard',
      'Trade': 'Trade',
      'fx-external': 'FX',
      'Bond': 'Bond',
      'ETF': 'ETF',
    };
    const route = routeMap[card.key as string] || 'Dashboard';
    onChooseProduct(route);
  };

  return (
    <div className="wh-page">
      <section className="wh-hero" aria-label="Welcome banner">
        <video
          autoPlay
          muted
          loop
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            zIndex: 0,
          }}
          onError={(e) => {
            console.log('Video failed to load: /trading-video.mp4');
          }}
        >
          <source src="/trading-video.mp4" type="video/mp4" />
        </video>
        <div className="wh-hero-overlay">
          <div className="wh-hero-badge">PRIVATE BETA</div>
          <h1 className="wh-hero-title">
            {greeting}{name ? `, ${name}` : ''}.
          </h1>
          <p className="wh-hero-sub">Choose your trading floor.</p>
        </div>
      </section>

      <div className="wh-container">
        <section className="wh-cards-large" aria-label="Primary products">
          {LARGE_CARDS.map((card) => (
            <button
              key={card.key}
              type="button"
              className="wh-card-large"
              onClick={() => handleLargeCard(card)}
              style={{ ['--wh-accent' as any]: card.accent }}
            >
              <div className="wh-card-icon" aria-hidden>{card.icon}</div>
              <div className="wh-card-body">
                <div className="wh-card-title">{card.title}</div>
                <div className="wh-card-subtitle">{card.subtitle}</div>
                <div className="wh-card-description">{card.description}</div>
              </div>
              <div className="wh-card-cta" aria-hidden>Enter →</div>
            </button>
          ))}
        </section>

        <section className="wh-cards-small" aria-label="Operations">
          <h2 className="wh-section-heading">Operations</h2>
          <div className="wh-tiles">
            {SMALL_TILES.map((tile) => (
              <button
                key={tile.key}
                type="button"
                className="wh-tile-small"
                onClick={() => onChooseProduct(tile.key)}
              >
                <span className="wh-tile-icon" aria-hidden>{tile.icon}</span>
                <span className="wh-tile-title">{tile.title}</span>
              </button>
            ))}
          </div>
        </section>

        <footer className="wh-footer">
          <div className="wh-footer-meta">SEC-regulated &middot; NDPR-compliant &middot; 256-bit TLS</div>
          <div className="wh-footer-legal">
            <a href="legal/terms.html" target="_blank" rel="noopener">Terms of Service</a>
            <a href="legal/privacy.html" target="_blank" rel="noopener">Privacy Policy</a>
            <a href="legal/risk.html" target="_blank" rel="noopener">Risk Disclosure</a>
            <a href="legal/fx-risk.html" target="_blank" rel="noopener">FX Risk</a>
            <a href="legal/ndpr.html" target="_blank" rel="noopener">NDPR Compliance</a>
          </div>
          <div className="wh-footer-actions">
            <button type="button" className="wh-signout" onClick={() => auth.signOut()}>Sign out</button>
          </div>
        </footer>
      </div>
    </div>
  );
}

